import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const SIZES = [[600, "600px"], [900, "900px"], [1200, "1200px"]];
const RATIOS = [["free", "Free"], ["1", "1:1"], ["4/3", "4:3"], ["3/4", "3:4"]];
const ratioVal = (r) => (r === "free" ? null : r === "1" ? 1 : r === "4/3" ? 4 / 3 : 3 / 4);

/** Inline crop + resize before saving. `files` = File[] queue; onDone(dataUrl, file) per image; onCancel(). */
export default function ImageCropDialog({ files, onDone, onCancel, quality = 0.85 }) {
  const [idx, setIdx] = useState(0);
  const [img, setImg] = useState(null);
  const [box, setBox] = useState(null); // {x,y,w,h} in image px
  const [ratio, setRatio] = useState("free");
  const [maxPx, setMaxPx] = useState(1200);
  const [scale, setScale] = useState(1);
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const file = files?.[idx];

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file); const im = new Image();
    im.onload = () => { setImg(im); setBox({ x: 0, y: 0, w: im.width, h: im.height }); setScale(Math.min(1, 520 / im.width, 380 / im.height)); URL.revokeObjectURL(url); };
    im.src = url;
  }, [file]);

  useEffect(() => {
    const c = canvasRef.current; if (!c || !img || !box) return;
    c.width = img.width * scale; c.height = img.height * scale;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0, c.width, c.height);
    ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, box.x, box.y, box.w, box.h, box.x * scale, box.y * scale, box.w * scale, box.h * scale);
    ctx.strokeStyle = "#D46117"; ctx.lineWidth = 2; ctx.strokeRect(box.x * scale, box.y * scale, box.w * scale, box.h * scale);
    ctx.fillStyle = "#D46117"; ctx.fillRect((box.x + box.w) * scale - 8, (box.y + box.h) * scale - 8, 8, 8);
  }, [img, box, scale]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e; return { x: (p.clientX - r.left) / scale, y: (p.clientY - r.top) / scale }; };
  const clamp = (b) => { const w = Math.max(20, Math.min(b.w, img.width)), h = Math.max(20, Math.min(b.h, img.height)); return { x: Math.max(0, Math.min(b.x, img.width - w)), y: Math.max(0, Math.min(b.y, img.height - h)), w, h }; };
  const applyRatio = (b, r) => { const rv = ratioVal(r); if (!rv) return b; return { ...b, h: b.w / rv }; };
  const down = (e) => { if (!box) return; const p = pos(e); const nearCorner = Math.abs(p.x - (box.x + box.w)) < 16 / scale && Math.abs(p.y - (box.y + box.h)) < 16 / scale; drag.current = { mode: nearCorner ? "resize" : "move", start: p, box: { ...box } }; };
  const move = (e) => { if (!drag.current) return; const p = pos(e); const d = drag.current; const dx = p.x - d.start.x, dy = p.y - d.start.y;
    if (d.mode === "move") setBox(clamp({ ...d.box, x: d.box.x + dx, y: d.box.y + dy }));
    else setBox(clamp(applyRatio({ ...d.box, w: d.box.w + dx, h: d.box.h + dy }, ratio))); };
  const up = () => { drag.current = null; };
  const setRatioAndBox = (r) => { setRatio(r); if (box) setBox(clamp(applyRatio(box, r))); };
  const reset = () => img && setBox({ x: 0, y: 0, w: img.width, h: img.height });

  const confirm = () => {
    const s = Math.min(1, maxPx / Math.max(box.w, box.h));
    const out = document.createElement("canvas"); out.width = Math.round(box.w * s); out.height = Math.round(box.h * s);
    const ctx = out.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(img, box.x, box.y, box.w, box.h, 0, 0, out.width, out.height);
    onDone(out.toDataURL("image/jpeg", quality), file);
    if (idx + 1 < files.length) { setIdx(idx + 1); setImg(null); } else onCancel();
  };

  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-2xl" data-testid="image-crop-modal">
        <DialogHeader><DialogTitle className="font-serif">Crop &amp; resize {files?.length > 1 ? `(${idx + 1} of ${files.length})` : ""}</DialogTitle><DialogDescription>Drag to move the crop box, drag the orange corner to resize. Choose an output size, then save.</DialogDescription></DialogHeader>
        <div className="flex justify-center rounded-lg bg-slate-100 p-2">
          <canvas ref={canvasRef} data-testid="image-crop-canvas" className="max-w-full cursor-move touch-none rounded" onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up} onTouchStart={down} onTouchMove={move} onTouchEnd={up} />
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-[#28282E]">
          <span className="font-semibold">Ratio</span>
          {RATIOS.map(([v, l]) => <button key={v} data-testid={`crop-ratio-${v.replace("/", "-")}`} onClick={() => setRatioAndBox(v)} className={`rounded-full px-3 py-1 font-semibold ${ratio === v ? "bg-[#28282E] text-white" : "bg-slate-100"}`}>{l}</button>)}
          <span className="ml-3 font-semibold">Max size</span>
          {SIZES.map(([v, l]) => <button key={v} data-testid={`crop-size-${v}`} onClick={() => setMaxPx(v)} className={`rounded-full px-3 py-1 font-semibold ${maxPx === v ? "bg-[#28282E] text-white" : "bg-slate-100"}`}>{l}</button>)}
          <button data-testid="crop-reset" onClick={reset} className="ml-auto underline">Reset crop</button>
          {box && <span className="text-[#28282E]/60">{Math.round(box.w)} × {Math.round(box.h)} px</span>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button data-testid="crop-confirm" onClick={confirm} disabled={!img} className="bg-[#28282E] text-white hover:bg-[#1F1F24]">{files?.length > 1 && idx + 1 < files.length ? "Save & next" : "Save image"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
